export type FormatOptions = {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  maxLength?: number;
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact';
  compactDisplay?: 'short' | 'long';
  currency?: string;
  style?: 'decimal' | 'currency' | 'percent';
  locale?: string;
  useGrouping?: boolean;
  preventRounding?: boolean; // Added this option instead of roundingMode
};

/**
 * Formats a number into a readable string with various formatting options
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted string representation of the number
 */
export const formatReadableNumber = (
  value: number | string | null | undefined,
  options: FormatOptions = {},
): string => {
  // Default options
  const defaultOptions: FormatOptions = {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    maxLength: 13,
    notation: 'standard',
    locale: 'en-US',
    style: 'decimal',
    useGrouping: true,
    preventRounding: false,
  };

  // Merge default options with provided options
  const finalOptions = { ...defaultOptions, ...options };

  try {
    // Convert input to number and handle invalid inputs
    const numericValue = Number(value);

    if (value === null || value === undefined || value === '' || isNaN(numericValue)) {
      return '0';
    }

    // Handle extremely large or small numbers
    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    // If preventRounding is true, manually truncate the number to the desired decimal places
    let valueToFormat = numericValue;
    if (finalOptions.preventRounding) {
      const factor = Math.pow(10, finalOptions.maximumFractionDigits || 0);
      valueToFormat = Math.floor(numericValue * factor) / factor;
    }

    // Format the number using Intl.NumberFormat
    const formatter = new Intl.NumberFormat(finalOptions.locale, {
      style: finalOptions.style,
      notation: finalOptions.notation,
      maximumFractionDigits: finalOptions.maximumFractionDigits,
      minimumFractionDigits: finalOptions.minimumFractionDigits,
      compactDisplay: finalOptions.compactDisplay,
      useGrouping: finalOptions.useGrouping,
      ...(finalOptions.currency && { currency: finalOptions.currency }),
    });

    let formattedValue = formatter.format(valueToFormat);

    // Truncate if exceeds maxLength
    if (finalOptions.maxLength && formattedValue.length > finalOptions.maxLength) {
      if (finalOptions.notation === 'compact') {
        // Already in compact form, just truncate
        formattedValue = formattedValue.slice(0, finalOptions.maxLength);
      } else {
        // Switch to compact notation
        const compactFormatter = new Intl.NumberFormat(finalOptions.locale, {
          notation: 'compact',
          maximumFractionDigits: 1,
        });

        // Apply truncation before formatting if needed
        let compactValue = numericValue;
        if (finalOptions.preventRounding) {
          compactValue = Math.floor(numericValue * 10) / 10;
        }

        formattedValue = compactFormatter.format(compactValue);
      }
    }

    return formattedValue;
  } catch (error) {
    console.error('Error formatting number:', error);
    return '0';
  }
};

/**
 * Helper function to format currency values
 */
export const formatCurrency = (
  value: number | string | null | undefined,
  currency = 'USD',
  options: Omit<FormatOptions, 'style' | 'currency'> = {},
): string => {
  return formatReadableNumber(value, {
    ...options,
    style: 'currency',
    currency,
    preventRounding: options.preventRounding ?? true, // Default to preventing rounding for currency
  });
};

/**
 * Helper function to format percentages
 */
export const formatPercentage = (
  value: number | string | null | undefined,
  options: Omit<FormatOptions, 'style'> = {},
): string => {
  return formatReadableNumber(value, {
    ...options,
    style: 'percent',
    maximumFractionDigits: options.maximumFractionDigits ?? 1,
    preventRounding: options.preventRounding ?? true, // Default to preventing rounding for percentages
  });
};
